/**
 * PSIFY IMMERSIVE — Soundscape (Web Audio)
 * Ambient generat sintetic — fără fișiere externe.
 *
 * Comportament:
 *  - OPRIT implicit, opt-in din toggle (persistat în localStorage). Până pe 23.09.2026 pornea singur la primul
 *    scroll; cineva venit dintr-o reclamă, cu telefonul pe difuzor, nu se așteaptă la un drone (owner, analiza paginii)
 *  - browserele cer un gest → redarea se „armează" la primul pointerdown/wheel/touch
 *  - sunetul TRĂIEȘTE doar în capitolul Transformării: crește când începe
 *    experiența cu text, se stinge când particulele s-au liniștit (progres > 0.9)
 */

const STORAGE_KEY = 'psify-sound-enabled'
const TARGET_VOLUME = 0.14
const CALM_THRESHOLD = 0.9 // particulele s-au așezat — liniște

let audioCtx = null
let masterGain = null
let nodes = []
let enabled = true
let unlocked = false
let experienceActive = false
let experienceProgress = 0

function buildAmbient() {
  // Drone calm: două oscilatoare detunate + LFO pe volum, filtrate low-pass.
  audioCtx = new (window.AudioContext || window.webkitAudioContext)()

  masterGain = audioCtx.createGain()
  masterGain.gain.value = 0
  masterGain.connect(audioCtx.destination)

  const filter = audioCtx.createBiquadFilter()
  filter.type = 'lowpass'
  filter.frequency.value = 320
  filter.Q.value = 0.4
  filter.connect(masterGain)

  const freqs = [110, 164.81, 220] // A2, E3, A3 — acord deschis, calm
  freqs.forEach((f, i) => {
    const osc = audioCtx.createOscillator()
    osc.type = 'sine'
    osc.frequency.value = f
    osc.detune.value = (i - 1) * 4

    const g = audioCtx.createGain()
    g.gain.value = i === 0 ? 0.5 : 0.22

    // respirația volumului — LFO lent, desincronizat pe voce
    const lfo = audioCtx.createOscillator()
    lfo.frequency.value = 0.045 + i * 0.02
    const lfoGain = audioCtx.createGain()
    lfoGain.gain.value = 0.08
    lfo.connect(lfoGain)
    lfoGain.connect(g.gain)

    osc.connect(g)
    g.connect(filter)
    osc.start()
    lfo.start()
    nodes.push(osc, lfo)
  })
}

function fadeTo(value, seconds) {
  if (!masterGain) return
  const now = audioCtx.currentTime
  masterGain.gain.cancelScheduledValues(now)
  masterGain.gain.setValueAtTime(masterGain.gain.value, now)
  masterGain.gain.linearRampToValueAtTime(value, now + seconds)
}

export function initSoundscape(toggleBtn) {
  // OPRIT implicit; '1' explicit = pornit de utilizator din toggle
  enabled = localStorage.getItem(STORAGE_KEY) === '1'

  const setUi = () => {
    if (!toggleBtn) return
    toggleBtn.classList.toggle('is-active', enabled)
    toggleBtn.setAttribute('aria-pressed', enabled ? 'true' : 'false')
    const label = enabled ? toggleBtn.dataset.labelOff : toggleBtn.dataset.labelOn
    if (label) toggleBtn.setAttribute('aria-label', label)
  }

  // starea dorită: sunet DOAR în experiență, cât timp haosul încă respiră
  const updatePlayback = () => {
    const shouldPlay = enabled && unlocked && experienceActive && experienceProgress < CALM_THRESHOLD

    if (shouldPlay) {
      if (!audioCtx) buildAmbient()
      if (audioCtx.state === 'suspended') audioCtx.resume()
      fadeTo(TARGET_VOLUME, 2.5)
    } else if (audioCtx) {
      // liniștea vine lent — ca o expirație
      fadeTo(0, 2)
    }
  }

  if (toggleBtn) {
    toggleBtn.addEventListener('click', () => {
      enabled = !enabled
      localStorage.setItem(STORAGE_KEY, enabled ? '1' : '0')
      unlocked = true // click-ul e un gest valid
      updatePlayback()
      setUi()
    })
  }
  setUi()

  // armare la primul gest (cerință browser pentru AudioContext)
  const unlock = () => {
    if (unlocked) return
    unlocked = true
    updatePlayback()
  }
  ;['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach((ev) =>
    window.addEventListener(ev, unlock, { once: true, passive: true })
  )

  // pauză când tab-ul nu e vizibil
  document.addEventListener('visibilitychange', () => {
    if (!audioCtx) return
    if (document.hidden) fadeTo(0, 0.5)
    else updatePlayback()
  })

  return {
    // capitolul Transformării intră/iese din viewport
    setExperienceActive(active) {
      experienceActive = active
      updatePlayback()
    },
    // progresul haos → calm (0..1)
    setExperienceProgress(p) {
      experienceProgress = p
      updatePlayback()
    }
  }
}
